import { DBWaContactsCacheThunks } from "@common-reducers/DBServiceThunks";
import { useAppDispatch, useAppSelector, useDebounce } from "@common-reducers/hooks/store.hook";
import { selectFilteredContactsMinimal, shouldGetMoreContactsSelector } from "@common-reducers/WhatsAppSelectors";
import { isExtensionContext } from "@common-services/detect-context";
import { profilePicExpiredEmitter } from "@extension/context/content/hooks/profilePicExpiredEmitter";
import { profilePicFetchRegistry } from "@extension/context/content/hooks/profilePicFetchRegistry";
import { fetchAndCacheProfilePicUrlsThunk, loadAllContactsAndChatsInExtensionThunk } from "@extension/context/content/thunks/WhatsappThunks";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const SEARCH_DEBOUNCE_MS = 800;
const RANGE_DEBOUNCE_MS = 150;
const IMAGE_BATCH_SIZE = 20;
const RANGE_BUFFER_BEFORE = 5;
const RANGE_BUFFER_AFTER = 15;

type ContactTypeFilter = 'all' | 'contacts' | 'groups' | 'channels';

interface UseWaContactsLazyLoadProps {
    searchFilter: string;
    typeFilter?: ContactTypeFilter;
    enabled?: boolean;
}

export const useWaContactsLazyLoad = ({
    searchFilter,
    typeFilter = 'all',
    enabled = true
}: UseWaContactsLazyLoadProps) => {
    const dispatch = useAppDispatch();
    const isExtension = isExtensionContext();

    const isItemsLoaded = useAppSelector(state => state.DBWaContactsCacheReducer.itemsLoaded);
    const itemsLoading = useAppSelector(state => state.DBWaContactsCacheReducer.itemsLoading);
    const shouldGetMoreContacts = useAppSelector(shouldGetMoreContactsSelector);
    const paginationSkip = useAppSelector(state => state.DBWaContactsCacheReducer.skip);
    const paginationLimit = useAppSelector(state => state.DBWaContactsCacheReducer.limit);
    const paginationTotal = useAppSelector(state => state.DBWaContactsCacheReducer.total);

    const debouncedSearchFilter = useDebounce(searchFilter, SEARCH_DEBOUNCE_MS);
    const skipSort = !isExtension;
    const filteredList = useAppSelector(state => selectFilteredContactsMinimal(state, debouncedSearchFilter, skipSort, typeFilter));

    // Track visible range so profile pic fetch targets what the user actually sees.
    const rangeRef = useRef({ startIndex: 0, endIndex: IMAGE_BATCH_SIZE });
    const [rangeVersion, setRangeVersion] = useState(0);
    const rangeTimerRef = useRef<ReturnType<typeof setTimeout>>();

    const handleRangeChanged = useCallback(({ startIndex, endIndex }: { startIndex: number; endIndex: number }) => {
        rangeRef.current = { startIndex, endIndex };
        if (rangeTimerRef.current) clearTimeout(rangeTimerRef.current);
        rangeTimerRef.current = setTimeout(() => setRangeVersion(v => v + 1), RANGE_DEBOUNCE_MS);
    }, []);

    useEffect(() => () => {
        if (rangeTimerRef.current) clearTimeout(rangeTimerRef.current);
    }, []);

    const handleEndReached = useCallback(() => {
        const nextSkip = paginationSkip + paginationLimit;
        const hasMoreItems = nextSkip < paginationTotal;

        if (enabled && filteredList.length > 0 && !itemsLoading && isItemsLoaded && !isExtension && hasMoreItems) {
            dispatch(DBWaContactsCacheThunks.find({
                $skip: nextSkip,
                $limit: 20,
                search: debouncedSearchFilter || undefined,
                $forceContactsUpdate: false,
                ...(typeFilter !== 'all' && { typeFilter }),
            }));
        }
    }, [dispatch, filteredList.length, isItemsLoaded, itemsLoading, debouncedSearchFilter, enabled, isExtension, paginationSkip, paginationLimit, paginationTotal, typeFilter]);

    // Initial/search-driven fetch.
    // We deliberately do NOT use `cleanStateBeforeFetch: true` here. That used
    // to wipe the entire DBWaContactsCacheReducer before each search, which
    // also blew away the cached imgUrl of contacts that are displayed in OTHER
    // surfaces (e.g. audience board rows). Those cells then lost their avatars
    // and fell back to the default-user SVG until a full refresh. The display
    // filtering is already client-side via `filteredList` / `selectFilteredContactsMinimal`,
    // so the cache can accumulate safely across searches.
    // Extension: load the full contacts/chats set ONCE on enable. Display filtering for search
    // and typeFilter is already client-side (`selectFilteredContactsMinimal` above), so we must
    // NOT re-run this on every search keystroke — `loadAllContactsAndChatsInExtensionThunk` does a
    // live `waQueryContactsAndChatsTool({ limit: -1 })` (the user's whole address book, 6k+ items),
    // and firing it per-search made the picker flicker (list empties while the reload runs, then
    // repopulates) and feel broken. Loading once + filtering the cache locally fixes that.
    useEffect(() => {
        if (!enabled || !isExtension) return;
        dispatch(loadAllContactsAndChatsInExtensionThunk());
    }, [enabled, isExtension, dispatch]);

    // Website: paginated backend-cache query — lightweight (reads the cached doc, no live WA pull),
    // so it can re-run on search/type changes to fetch the next matching page.
    useEffect(() => {
        if (!enabled || isExtension) return;
        dispatch(DBWaContactsCacheThunks.find({
            $skip: 0,
            $limit: 20,
            search: debouncedSearchFilter || undefined,
            ...(typeFilter !== 'all' && { typeFilter }),
        }));
    }, [debouncedSearchFilter, dispatch, enabled, isExtension, typeFilter]);

    // When ContactAvatar detects an expired URL, it clears imgUrl from Redux
    // (via clearExpiredProfilePicThunk) and emits here so we allow one re-request.
    useEffect(() => {
        return profilePicExpiredEmitter.subscribe((contactId: string) => {
            profilePicFetchRegistry.allowRetry(contactId);
        });
    }, []);

    // IDs in the current visible window that still need an image fetch.
    // Uses the global set so multiple hook instances don't duplicate requests.
    const contactsNeedingImageIds = useMemo(() => {
        if (!isItemsLoaded || filteredList.length === 0) return [] as string[];

        const { startIndex, endIndex } = rangeRef.current;
        const start = Math.max(0, startIndex - RANGE_BUFFER_BEFORE);
        const end = Math.min(filteredList.length, endIndex + RANGE_BUFFER_AFTER + 1);

        const ids: string[] = [];
        for (let i = start; i < end && ids.length < IMAGE_BATCH_SIZE; i++) {
            const contact = filteredList[i];
            const serializedId = contact?.id?._serialized;
            if (!serializedId) continue;
            if (profilePicFetchRegistry.has(serializedId)) continue;
            // Has a pic URL → skip
            if (contact.imgUrl) continue;
            // Was checked recently (within TTL) but had no pic → skip
            if (profilePicFetchRegistry.isFresh(contact.profilePicLastUpdate)) continue;
            // Never checked, or TTL expired → fetch
            ids.push(serializedId);
        }
        return ids;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filteredList, isItemsLoaded, rangeVersion]);

    const contactsNeedingImagesKey = contactsNeedingImageIds.join(',');

    // Fetch profile pics for visible contacts.
    // Double-check the global set at execution time to prevent duplicate
    // requests from concurrent renders or multiple hook instances.
    useEffect(() => {
        if (!enabled || contactsNeedingImageIds.length === 0) return;

        const idsToFetch = contactsNeedingImageIds.filter(id => !profilePicFetchRegistry.has(id));
        if (idsToFetch.length === 0) return;

        idsToFetch.forEach(id => profilePicFetchRegistry.add(id));

        dispatch(fetchAndCacheProfilePicUrlsThunk(idsToFetch)).unwrap()
            .catch(() => {
                idsToFetch.forEach(id => profilePicFetchRegistry.remove(id));
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactsNeedingImagesKey, enabled, dispatch]);

    return {
        filteredList,
        handleEndReached,
        handleRangeChanged,
        isItemsLoaded,
        itemsLoading,
        shouldGetMoreContacts,
        totalCount: paginationTotal,
        hasMoreItems: (paginationSkip + paginationLimit) < paginationTotal
    };
};
